import { useForm } from '@tanstack/react-form'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Grade } from '@/interface/types'
import { useTranslation } from 'react-i18next'
import { useUser } from '@/hooks/useUser'
const gradeSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  establishmentId: z.string().optional(),
  etablissementId: z.string().optional(),
})

export type GradeFormValues = z.infer<typeof gradeSchema>

interface GradeFormProps {
  initialData?: Partial<Grade>
  onSubmit: (data: GradeFormValues) => Promise<void>
  onCancel: () => void
  submitLabel: string
}

/**
 * A reusable form component for creating and editing grades
 * @param {GradeFormProps} props - The props for the GradeForm component
 * @returns {JSX.Element} The rendered GradeForm component
 */
export function GradeForm({
  initialData,
  onSubmit,
  onCancel,
}: GradeFormProps) {
  const { user } = useUser()
  // Get establishmentId from userStore (fetched from API via HTTP-only cookies)
  const etablissementId = user?.establishmentId || ''

  const form = useForm({
    defaultValues: {
      name: initialData?.name || '',
      establishmentId: initialData?.establishmentId || etablissementId,
      etablissementId: initialData?.etablissementId || etablissementId,
    },
    onSubmit: async ({ value }) => {
      await onSubmit(value)
    },
  })

  const { t } = useTranslation('admin', { keyPrefix: 'etablissment-folder.gradeForm' })
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        e.stopPropagation()
        void form.handleSubmit()
      }}
      className="space-y-6"
    >
      <div className="grid grid-cols-1 gap-4">
        <form.Field
          name="name"
          validators={{
            onChange: ({ value }) => {
              if (!value) return t('fields.name.validation.required')
              return undefined
            },
          }}
        >
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor="name">{t('fields.name.label')}</Label>
              <Input
                id="name"
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                placeholder={t('fields.name.placeholder')}
              />
              {field.state.meta.errors && (
                <p className="text-sm text-red-500">
                  {field.state.meta.errors.join(', ')}
                </p>
              )}
            </div>
          )}
        </form.Field>
      </div>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          {t('buttons.cancel')}
        </Button>
        <Button type="submit">{t('buttons.submit')}</Button>
      </div>
    </form>
  )
}
